/**
 * Mission Control auth context (Phase 2 Action 27).
 *
 * Holds the operator session in memory + localStorage so a page reload
 * keeps the user signed in. Exposes:
 *   useAuth() → { operator, isLoading, signIn, signOut }
 *
 * Storage strategy — localStorage:
 *   - Simple, survives page reloads
 *   - XSS risk is the tradeoff; Mission Control renders no untrusted
 *     content from tenants, so the surface is minimal
 *   - Future migration to httpOnly cookies is a self-contained change
 *     (this file + the backend's Set-Cookie header)
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { apiPath } from '../api/baseUrl';

export interface Operator {
  id: string;
  email: string;
  role: 'read_only' | 'support' | 'admin';
}

interface AuthContextValue {
  operator: Operator | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'mc.token';
const OPERATOR_KEY = 'mc.operator';

function readStoredOperator(): Operator | null {
  try {
    const raw = localStorage.getItem(OPERATOR_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Operator;
  } catch {
    return null;
  }
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearStoredSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(OPERATOR_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [operator, setOperator] = useState<Operator | null>(() => readStoredOperator());
  const [isLoading, setIsLoading] = useState<boolean>(() => Boolean(getStoredToken()));

  // On mount, if we have a token but want to verify it's still valid,
  // ping /api/auth/me. If it 401s, clear the session.
  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    fetch(apiPath('/api/auth/me'), { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (res.status === 200) {
          const body = (await res.json()) as { data: { operator: Operator } };
          setOperator(body.data.operator);
          localStorage.setItem(OPERATOR_KEY, JSON.stringify(body.data.operator));
        } else {
          clearStoredSession();
          setOperator(null);
        }
      })
      .catch(() => {
        clearStoredSession();
        setOperator(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await fetch(apiPath('/api/auth/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      let message = 'Sign-in failed';
      try {
        const body = (await res.json()) as { message?: string };
        if (body.message) message = body.message;
      } catch {
        // ignore JSON parse errors — keep default message
      }
      throw new Error(message);
    }
    const body = (await res.json()) as { data: { token: string; operator: Operator } };
    localStorage.setItem(TOKEN_KEY, body.data.token);
    localStorage.setItem(OPERATOR_KEY, JSON.stringify(body.data.operator));
    setOperator(body.data.operator);
  }, []);

  const signOut = useCallback(() => {
    const token = getStoredToken();
    // Fire-and-forget — the local session clears regardless.
    if (token) {
      fetch(apiPath('/api/auth/logout'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {
        // ignore — logout is a no-op on the server
      });
    }
    clearStoredSession();
    setOperator(null);
  }, []);

  return (
    <AuthContext.Provider value={{ operator, isLoading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be used inside <AuthProvider>');
  return ctx;
}
